'use client';

import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { MAIN_SECTION_PORTAL_TARGET_ID } from '@/constants';

export interface ReviewDialogProps {
  open: boolean;
  isShowCancelButton: boolean;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string;
}

export default function ReviewDialog({
  open,
  isShowCancelButton,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  title,
  message,
}: ReviewDialogProps) {
  // 모바일 컨테이너(main-section-portal-target)로 포털링해 viewport 기준으로 띄운다.
  // 인라인 absolute로 두면 긴 스크롤 콘텐츠 컨테이너 기준으로 위치가 잡혀
  // 팝업이 화면 밖에 위치(스크롤해야 보임)하는 문제가 있었다.
  // AlertModal과 동일한 패턴(포털 + 배경 스크롤락 + 중앙정렬)으로 통일한다.
  const portalTarget =
    open && typeof document !== 'undefined'
      ? document.getElementById(MAIN_SECTION_PORTAL_TARGET_ID)
      : null;

  // 다이얼로그가 열려 있는 동안 배경 스크롤을 잠그고 스크롤 위치를 보존한다.
  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflowY = 'scroll';
      document.body.style.width = '100%';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.overflowY = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  if (!open) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onCancel();
  };

  // 포털 컨테이너 안에서는 absolute(컨테이너가 viewport 영역), 폴백은 fixed.
  const overlayClassName = portalTarget
    ? 'absolute inset-0 z-150 flex items-center justify-center'
    : 'fixed inset-0 z-150 flex items-center justify-center';

  const content = (
    <div className={overlayClassName}>
      {/* 배경 딤 */}
      <div className="absolute inset-0 bg-black/60" aria-hidden="true" />

      {/* 다이얼로그 박스 (디자인 동일) */}
      <div className="relative z-10 flex w-[244px] flex-col gap-5 bg-white px-5 pt-7 pb-5 rounded-lg">
        <div className="flex flex-col items-center justify-center gap-2">
          <span className="text-center typo-16 font-bold text-222222">
            {title}
          </span>
          {message && (
            <span className="text-center typo-13 font-semibold text-888888">
              {message}
            </span>
          )}
        </div>

        <div className={`flex ${isShowCancelButton ? 'gap-1' : ''}`}>
          {isShowCancelButton && (
            <button
              className="flex-1 bg-[#EFEEF7] text-[#908E9E] typo-14 font-medium py-[9px] cursor-pointer"
              onClick={handleCancel}
            >
              {cancelText}
            </button>
          )}
          <button
            className={`${isShowCancelButton ? 'flex-1' : 'w-full'} bg-252530 text-white typo-14 font-medium py-[9px] cursor-pointer`}
            onClick={handleConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  if (portalTarget) {
    return createPortal(content, portalTarget);
  }
  return content;
}
